import { Cars, Flights, Hotels } from 'pcln-icons'
import { EnumLiteralsOf } from '@/types'

export const Product = {
  Flight: 1,
  Hotel: 5,
  Car: 8
} as const

export type ProductType = EnumLiteralsOf<typeof Product>

type Product = 'hotel' | 'flight' | 'car'

export type CrossSellContentBlock = {
  product: Product
  xSellMessaging: string
  xSellTarget: 'Rental Cars' | 'Hotels' | 'Flights'
  xSellTargetId: ProductType
  date: string
  destination: string
  headline: string
  subHeadline: string
  xSellUrl: string
  offerToken: string
}

type CommonBookingProperties = {
  offerId: string
  offerToken: string
  secureToken: string
  productId: ProductType
  travelStartDateTime: string
  travelEndDateTime: string
  originLocationCode: string
  destinationLocationCode: string
  destinationCity: string
  destinationCountryCode: string
  utcCheckInDateTime: string
  hotelId: string
  hotelNumRooms: number
  hotelName: string
}

type Pricing = {
  currencySymbol: string
  disclaimer: string
  price: string
  priceDescription: string
}

type Partner = {
  name?: string
  url?: string
}

type VehicleFeatures = {
  bagCapacity: number
  isAirConditioned: boolean
  isUnlimitedMileage: boolean
  mileageDistance: string
  mileageUnit: string
  peopleCapacity: number
}

type Rate = {
  currencyCode: string
  currencySymbol: string
  dailyPrice: string
  isFreeCancellation: boolean
  isPrepay: boolean
  savingsPriceTotal: string
  strikeThroughPrice: string
  totalPrice: string
}

type Inventory<T> = {
  listings: T[]
}

export type HotelInventory = Inventory<HotelListing>

export type CarInventory = Inventory<CarListing>

export type CarListing = {
  offerToken: string
  bannerType: string
  carExample: string
  carImageUrl: string
  href: string
  id: string
  isCarExampleExact: boolean
  options: string[]
  partner: Partner
  pricing: Pricing
  rate: Rate
  checkoutUrl: string
  vehicleFeatures: VehicleFeatures
}

export type HotelListing = {
  offerToken: string
  currency: string
  currencySymbol: string
  currentPrice: number
  guestRating: number
  guestReviewCount: number
  hotelId: string
  href: string
  imageUrl: string
  location: string
  name: string
  originalPrice: number
  savingPercentage: number | null
  starRating: number
}

export type CarInventoryParams = {
  returnLocation: string
  pickupLocation: string
  returnDateTime: string
  pickupDateTime: string
  tokenType: string
  offerToken: string
}

export type HotelItineraryDetails = {
  cityId: number
  cityName: string
  checkIn: number
  checkOut: number
}

export type HotelInventoryParams = HotelItineraryDetails & {
  appc: string
  cguid: string
  dealTypes: boolean
  rid: string
  productId: number
  pageSize: number
}

export type HotelBooking = { productId: typeof Product.Hotel } & Omit<
  CommonBookingProperties,
  'originLocationCode'
>
export type FlightBooking = { productId: typeof Product.Flight } & Omit<
  CommonBookingProperties,
  | 'utcCheckInDateTime'
  | 'destinationCountryCode'
  | 'hotelName'
  | 'hotelId'
  | 'hotelNumRooms'
>
export type CarBooking = { productId: typeof Product.Car } & Omit<
  CommonBookingProperties,
  | 'originLocationCode'
  | 'destinationLocationCode'
  | 'destinationCountryCode'
  | 'hotelName'
  | 'hotelId'
  | 'hotelNumRooms'
>
export type Booking = HotelBooking | FlightBooking | CarBooking
export type Listing = CarListing | HotelListing

export type ProductIconType = typeof Flights | typeof Cars | typeof Hotels

export type TripDetailsBoxProps = {
  product: string
  headline: string
  subHeadline: string
}
